/**
 * Doctor specialty drives what each person sees (medopd-docs 155). Pure rules, no React: the server sends
 * `doctorSpecialty` and `specialtyFeatures` on GET /auth/my-permissions, and `doctorSpecialty` (the visit's doctor) on
 * appointment / token payloads. Everything here treats a missing field (`undefined`, an older server) as "not told",
 * so callers fall back to the behaviour before doc 155 instead of hiding things.
 *
 * Specialty keys are the specialty-pack keys (`SpecialtyTemplateKeys` on the server) plus GENERAL.
 */
import type { ClinicalImageCategory } from './api/modules/clinicalImages';

export const DENTAL_SPECIALTY = 'DENTAL';
export const EYE_SPECIALTY = 'OPHTHALMOLOGY';
export const GENERAL_SPECIALTY = 'GENERAL';

/** Every specialty key the web knows a label for (common:specialty.<key>). The server list is authoritative. */
export const KNOWN_SPECIALTY_KEYS = [
  'GENERAL', 'DENTAL', 'OPHTHALMOLOGY', 'CARDIOLOGY', 'ENDOCRINOLOGY', 'GYNECOLOGY_OB', 'NEPHROLOGY', 'NEUROLOGY',
  'NEUROSURGERY', 'ORTHOPAEDICS', 'PEDIATRICS', 'RHEUMATOLOGY',
] as const;

/** The clinical specialty areas a clinic can have on, and that a person may see. */
export const SPECIALTY_FEATURES = ['DENTAL', 'EYE_CARE', 'OPTICAL', 'WORK_ORDERS'] as const;
export type SpecialtyFeature = (typeof SPECIALTY_FEATURES)[number];

/** What GET /auth/my-permissions adds (medopd-docs 155 S2). Both absent on an older server. */
export interface SpecialtyAccessFields {
  doctorSpecialty?: string | null;
  specialtyFeatures?: string[] | null;
}

export interface SpecialtyAccess {
  /** The server sent `specialtyFeatures` — when false, every rule below falls back to the old behaviour. */
  present: boolean;
  /** The signed-in doctor's specialty key in the active clinic; null for non-doctors (or when not told). */
  doctorSpecialty: string | null;
  /** The specialty areas this person sees; null when the server did not say. */
  features: readonly string[] | null;
}

export function readSpecialtyAccess(data: SpecialtyAccessFields | null | undefined): SpecialtyAccess {
  const features = Array.isArray(data?.specialtyFeatures) ? data!.specialtyFeatures! : null;
  return {
    present: features !== null,
    doctorSpecialty: typeof data?.doctorSpecialty === 'string' && data.doctorSpecialty ? data.doctorSpecialty : null,
    features,
  };
}

/** Whether this person sees a specialty area. Not told by the server → true (the clinic module still gates it). */
export function seesFeature(features: readonly string[] | null | undefined, feature: SpecialtyFeature): boolean {
  if (!features) return true;
  return features.includes(feature);
}

/** Pages that belong to one specialty area. */
export const PATH_FEATURES: Record<string, SpecialtyFeature> = {
  '/dental': 'DENTAL',
  '/eye-care': 'EYE_CARE',
  '/optical': 'OPTICAL',
  '/work-orders': 'WORK_ORDERS',
};

export function featureForPath(path: string): SpecialtyFeature | null {
  for (const [prefix, feature] of Object.entries(PATH_FEATURES)) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return feature;
  }
  return null;
}

/** The side menu and dashboard buttons: a specialty page only for people who see that area. */
export function specialtyAllowsPath(path: string, features: readonly string[] | null | undefined): boolean {
  const feature = featureForPath(path);
  return !feature || seesFeature(features, feature);
}

/** Who a specialty area is for, in words for the "this area is for …" page. */
export function audienceOf(feature: SpecialtyFeature): 'dentists' | 'eyeDoctors' | 'specialists' {
  if (feature === 'DENTAL') return 'dentists';
  if (feature === 'EYE_CARE' || feature === 'OPTICAL') return 'eyeDoctors';
  return 'specialists';
}

/**
 * The visit doctor's specialty from an appointment / token payload. `undefined` = the server did not send the field
 * (fall back); `null` = sent but unknown; otherwise the key.
 */
export function visitSpecialty(appointment: { doctorSpecialty?: string | null } | null | undefined): string | null | undefined {
  if (!appointment || !('doctorSpecialty' in appointment) || appointment.doctorSpecialty === undefined) return undefined;
  return appointment.doctorSpecialty || null;
}

export const isDentalSpecialty = (key: string | null | undefined) => key === DENTAL_SPECIALTY;

/**
 * A doctor whose specialty the server told us, and it is not `ownerSpecialty`: specialty records (the dental chart for
 * DENTAL, the eye exam for OPHTHALMOLOGY) are read-only for them (medopd-docs 155). Unknown specialty, other roles or an
 * older server → false (the server's own canEdit / permissions still decide).
 */
export function otherSpecialtyDoctor(role: string | null | undefined, access: Pick<SpecialtyAccess, 'present' | 'doctorSpecialty'>,
  ownerSpecialty: string): boolean {
  return role === 'doctor' && access.present && !!access.doctorSpecialty && access.doctorSpecialty !== ownerSpecialty;
}
export const isEyeSpecialty = (key: string | null | undefined) => key === EYE_SPECIALTY;

// ── Clinical image tags ───────────────────────────────────────────────────────────────────────────────────────

const DENTAL_IMAGE_CATEGORIES: readonly string[] = ['IOPA', 'BITEWING', 'OPG', 'CBCT', 'CEPH', 'INTRAORAL_PHOTO', 'EXTRAORAL_PHOTO'];
const EYE_IMAGE_CATEGORIES: readonly string[] = ['FUNDUS_PHOTO', 'OCT', 'VISUAL_FIELD', 'TOPOGRAPHY', 'SLIT_LAMP_PHOTO', 'BIOMETRY'];

export interface TagOptions {
  /** Dental categories and the FDI tooth field / picker. */
  dental: boolean;
  /** Eye categories and the eye (RE / LE / both) picker. */
  eye: boolean;
}

/**
 * Which tag fields to offer when tagging an image on a visit. `specialty` undefined (not told) → everything, as before
 * doc 155. Dental fields for dentists' visits, eye fields for eye doctors' visits, general categories for everyone.
 * An image that already carries a dental / eye tag keeps that group's fields, so editing never hides its tags.
 */
export function imageTagOptions(
  specialty: string | null | undefined,
  existing?: { category?: ClinicalImageCategory | string | null; teeth?: number[] | null; eye?: string | null },
): TagOptions {
  if (specialty === undefined) return { dental: true, eye: true };
  const cat = existing?.category ?? '';
  return {
    dental: isDentalSpecialty(specialty) || DENTAL_IMAGE_CATEGORIES.includes(cat) || (existing?.teeth?.length ?? 0) > 0,
    eye: isEyeSpecialty(specialty) || EYE_IMAGE_CATEGORIES.includes(cat) || !!existing?.eye,
  };
}

// ── Treatment plans ───────────────────────────────────────────────────────────────────────────────────────────

/** A treatment plan's specialty ("dental" / "ophthalmology") from a specialty key; undefined for everyone else. */
export function planSpecialtyFromKey(key: string | null | undefined): string | undefined {
  if (isDentalSpecialty(key)) return 'dental';
  if (isEyeSpecialty(key)) return 'ophthalmology';
  return undefined;
}

// ── Consent forms and recall rules ────────────────────────────────────────────────────────────────────────────

/** Consent / recall groups (GENERAL, DENTAL, EYE): GENERAL always; the others only for people who see that area. */
export function specialtyGroupVisible(group: string, features: readonly string[] | null | undefined): boolean {
  if (group === 'DENTAL') return seesFeature(features, 'DENTAL');
  if (group === 'EYE') return seesFeature(features, 'EYE_CARE');
  return true;
}

// ── Specialty pickers ─────────────────────────────────────────────────────────────────────────────────────────

/** One entry of GET /specialties (and the public list used by doctor sign-up). */
export interface SpecialtyOption {
  key: string;
  labelKey?: string;
  englishName: string;
  publicCatalogCodes?: string[];
  dentalCouncil?: boolean;
}

/** GENERAL first, then by English name — the order a picker shows. */
export function sortSpecialties(list: readonly SpecialtyOption[]): SpecialtyOption[] {
  return [...list].sort((a, b) => (a.key === GENERAL_SPECIALTY ? -1 : b.key === GENERAL_SPECIALTY ? 1 : a.englishName.localeCompare(b.englishName)));
}

/**
 * The departments a doctor of `specialtyKey` may be put in: departments with no specialty template (or a
 * non-specialty one), and those whose template is this specialty. A department of another specialty is left out.
 */
export function departmentsForSpecialty<D extends { templateKey?: string | null }>(
  departments: readonly D[], specialtyKey: string | null | undefined, specialtyKeys: readonly string[],
): D[] {
  const known = new Set(specialtyKeys);
  return departments.filter((d) => {
    const tk = d.templateKey ?? '';
    if (!tk || !known.has(tk) || tk === GENERAL_SPECIALTY) return true;
    return !!specialtyKey && tk === specialtyKey;
  });
}

/**
 * Thrown by the `onInvite` callers when the server refuses the invitation, carrying its error code so the dialog can
 * explain SPECIALTY_REQUIRED / DEPARTMENT_SPECIALTY_MISMATCH in plain words (medopd-docs 155 S6).
 */
export class InviteError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'InviteError';
  }
}

/** Invitation (S6): what is missing before a doctor can be invited. Only when the server offers specialties. */
export function inviteSpecialtyProblem(role: string, specialtyKey: string, specialtiesOffered: boolean): 'specialtyRequired' | null {
  return role === 'doctor' && specialtiesOffered && !specialtyKey ? 'specialtyRequired' : null;
}

/** The plain message key for an invitation refused by the server, from its error code (or the code in its text). */
export function inviteErrorKey(code: string | null | undefined, message?: string | null):
  'specialtyRequired' | 'departmentMismatch' | 'unknownSpecialty' | 'departmentNotFound' | null {
  const text = `${code ?? ''} ${message ?? ''}`;
  if (text.includes('SPECIALTY_REQUIRED')) return 'specialtyRequired';
  if (text.includes('DEPARTMENT_SPECIALTY_MISMATCH')) return 'departmentMismatch';
  if (text.includes('UNKNOWN_SPECIALTY')) return 'unknownSpecialty';
  if (text.includes('DEPARTMENT_NOT_FOUND')) return 'departmentNotFound';
  return null;
}

/** A department whose template is a specialty key fixes the doctor's specialty (S1: the department wins). */
export function specialtyFromDepartment(templateKey: string | null | undefined, specialtyKeys: readonly string[]): string | null {
  if (!templateKey || templateKey === GENERAL_SPECIALTY) return null;
  return specialtyKeys.includes(templateKey) ? templateKey : null;
}

/**
 * Doctor sign-up: the specialty key for what the doctor typed or picked. A public-catalogue entry (matched by name)
 * maps through `publicCatalogCodes`; otherwise a specialty whose English or shown name matches. Null when nothing
 * matches — the doctor then picks one (with "Other / General").
 */
export function resolveSpecialtyKey(
  text: string,
  catalogue: readonly { code: string; name: string }[],
  specialties: readonly SpecialtyOption[],
  labelOf?: (s: SpecialtyOption) => string,
): string | null {
  const q = text.trim().toLowerCase();
  if (!q) return null;
  const entry = catalogue.find((c) => c.name.trim().toLowerCase() === q || c.code.toLowerCase() === q);
  if (entry) {
    const byCode = specialties.find((s) => (s.publicCatalogCodes ?? []).some((c) => c.toLowerCase() === entry.code.toLowerCase()));
    if (byCode) return byCode.key;
  }
  const byName = specialties.find((s) => s.englishName.toLowerCase() === q || s.key.toLowerCase() === q
    || (labelOf ? labelOf(s).trim().toLowerCase() === q : false));
  return byName ? byName.key : null;
}

// ── Settings → Specialty features ─────────────────────────────────────────────────────────────────────────────

/** One row of GET /clinic/specialty-features. */
export interface SpecialtyFeatureRow {
  code: string;
  enabled: boolean;
  source?: string | null;
  lockedBySuperAdmin?: boolean;
  reasonKey?: string | null;
  reasonParams?: Record<string, unknown> | null;
  available?: boolean;
}

export type FeatureBlock = 'locked' | 'unavailable' | 'needsEyeCare' | null;

/** Why a row's switch cannot be changed, if it cannot. Locked by support beats everything else. */
export function featureBlock(row: SpecialtyFeatureRow, rows: readonly SpecialtyFeatureRow[]): FeatureBlock {
  if (row.lockedBySuperAdmin) return 'locked';
  if (row.code === 'OPTICAL' && !row.enabled && !rows.find((r) => r.code === 'EYE_CARE')?.enabled) return 'needsEyeCare';
  if (row.available === false && !row.enabled) return 'unavailable';
  return null;
}

/** "autoSpecialty" / "specialtyFeatures.reason.AUTO_SPECIALTY" → "AUTO_SPECIALTY". */
export function normaliseReasonKey(key: string): string {
  const last = key.split('.').pop() ?? key;
  return last.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[^A-Za-z0-9_]/g, '_').toUpperCase();
}
